/**
 * Points the Overview's grid stores at one dashboard (screenshots/84): its widgets, data and filters go into
 * overviewStore / overviewLayout (so the same Grid, widgets, chart settings, library, cross-filter and conflict merge
 * paint it), and layout saves go to `/api/dashboards/<id>` with the version they started from (409 → merge, §2.15).
 */
import type { UserGroup } from "../nav/navConfig";
import { connectionStore } from "../stores/connectionStore";
import { normalizeItems, overviewLayout, overviewStore, setLayoutSaver } from "../overview/overviewStore";
import type { LayoutItem, OverviewSnapshot } from "../overview/types";
import type { SaveResult } from "../overview/grid/layoutStore";
import { analyticsStore } from "./analyticsStore";
import { canEdit, type Dashboard, type Viewer } from "./dashboards";

async function putLayout(id: string, items: LayoutItem[], baseVersion: number, touched: string[]): Promise<SaveResult<LayoutItem>> {
  const r = await fetch(`/api/dashboards/${encodeURIComponent(id)}`, { method: "PUT", credentials: "include", headers: { "content-type": "application/json" },
    body: JSON.stringify({ baseVersion, items, touched }) });
  if (r.status === 409) {
    const c = await r.json() as { items: unknown[]; version: number; touched: string[]; by: string };
    return { ok: false, conflict: { layout: normalizeItems(c.items), version: c.version, touched: c.touched ?? [], by: c.by } };
  }
  if (!r.ok) throw new Error(String(r.status));
  const d = await r.json() as { version: number; dashboard?: Dashboard };
  if (d.dashboard) analyticsStore.getState().upsert(d.dashboard);
  return { ok: true, version: d.version };
}

/** Signed in: the dashboard's grid, editable when canEdit. */
export function openDashboard(d: Dashboard, grid: OverviewSnapshot, viewer: Viewer) {
  overviewLayout.getState().setCustomizing(false);
  overviewStore.getState().hydrate(grid, viewer.group, { host: `dashboard:${d.id}`, exact: true, filters: d.filters, readOnly: !canEdit(d, viewer) });
  overviewLayout.setState({ status: "saved", lastMerge: null });
  setLayoutSaver((items, base, touched) => putLayout(d.id, items, base, touched));
  connectionStore.setState({ status: "snapshot", snapshotAt: grid.at });
}

/** Public link (/d/<token>): read-only, filters as shared, no saves. */
export function openLink(token: string, grid: OverviewSnapshot, group: UserGroup, filters: Dashboard["filters"]) {
  overviewStore.getState().hydrate(grid, group, { host: `link:${token}`, exact: true, filters, readOnly: true });
  setLayoutSaver(async (_l, v) => ({ ok: true, version: v }));
  connectionStore.setState({ status: "snapshot", snapshotAt: grid.at });
}

/** Opening a dashboard without a page load (after Create, from the home): its grid data from the API. */
export async function fetchDashboard(id: string): Promise<{ d: Dashboard; grid: OverviewSnapshot } | { error: number }> {
  const r = await fetch(`/api/dashboards/${encodeURIComponent(id)}?grid=1`, { credentials: "include" }).catch(() => null);
  if (!r?.ok) return { error: r?.status ?? 500 };
  const { grid, ...d } = await r.json() as Dashboard & { grid: OverviewSnapshot };
  analyticsStore.getState().upsert(d);
  return { d, grid };
}
